<template>
  <div v-if="medical">
    <!-- Cover Section -->
    <div
      class="relative bg-no-repeat bg-center bg-cover grow flex flex-col-reverse pb-6 lg:pb-10 h-[10rem] lg:h-[15rem]"
      :style="{
        backgroundImage: `url(${medical.cover})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }"
    >
      <div class="absolute inset-0 bg-[#2C5BA1] bg-opacity-40"></div>
      <div class="absolute inset-0 flex flex-col items-center justify-center text-white text-3xl font-bold">
        <h1>
          <span v-if="currentLanguage === 'fr'">{{ medical.name }}</span>
          <span v-if="currentLanguage === 'ar'">{{ medical.name_ar }}</span>
          <span v-if="currentLanguage === 'en'">{{ medical.name_en }}</span>
          <span v-if="currentLanguage === 'ru'">{{ medical.name_ru }}</span>
          <span v-if="currentLanguage === 'zh'">{{ medical.name_zh }}</span>
          <span v-if="currentLanguage === 'ja'">{{ medical.name_ja }}</span>
          <span v-if="currentLanguage === 'ko'">{{ medical.name_ko }}</span>
        </h1>
        <div class="text-xs lg:text-base font-semibold text-white mt-4">
          <p class="flex flex-row gap-2">
            <a class="hover:underline" :href="`/`">{{ $t("home") }}</a>
            <span v-if="currentLanguage === 'ar'">/</span>
            <span v-if="currentLanguage !== 'ar'">\</span>
            <a class="hover:underline" :href="`/sejour`">{{ $t("sejour_medical") }}</a>
            <span v-if="currentLanguage === 'ar'">/</span>
            <span v-if="currentLanguage !== 'ar'">\</span>
            <span>
              <span v-if="currentLanguage === 'fr'">{{ medical.name }}</span>
              <span v-if="currentLanguage === 'ar'">{{ medical.name_ar }}</span>
              <span v-if="currentLanguage === 'en'">{{ medical.name_en }}</span>
              <span v-if="currentLanguage === 'ru'">{{ medical.name_ru }}</span>
              <span v-if="currentLanguage === 'zh'">{{ medical.name_zh }}</span>
              <span v-if="currentLanguage === 'ja'">{{ medical.name_ja }}</span>
              <span v-if="currentLanguage === 'ko'">{{ medical.name_ko }}</span>
            </span>
          </p>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="container p-3.5 sm:px-6 md:px-8 lg:px-16 xl:px-16 mx-auto my-8">
      <!-- Images Gallery -->
      <div v-if="medical.images?.length > 0" class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div
          v-for="(image, index) in medical.images.slice(0, 6)"
          :key="index"
          class="relative h-48 rounded-lg overflow-hidden"
        >
          <img
            :src="image"
            :alt="medical.name"
            class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
      </div>

      <!-- Details Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Left Column -->
        <div>
          <h2 class="text-2xl font-bold mb-4">{{ $t('description') }}</h2>
          <div class="mb-6">
            <span v-if="currentLanguage === 'fr'" v-html="medical.description"></span>
            <span v-if="currentLanguage === 'ar'" v-html="medical.description_ar"></span>
            <span v-if="currentLanguage === 'en'" v-html="medical.description_en"></span>
            <span v-if="currentLanguage === 'ru'" v-html="medical.description_ru"></span>
            <span v-if="currentLanguage === 'zh'" v-html="medical.description_zh"></span>
            <span v-if="currentLanguage === 'ja'" v-html="medical.description_ja"></span>
            <span v-if="currentLanguage === 'ko'" v-html="medical.description_ko"></span>
          </div>

          <h3 class="text-xl font-bold mb-4">{{ $t('activity') }}</h3>
          <div class="mb-6">
            <span v-if="currentLanguage === 'fr'" v-html="medical.activity"></span>
            <span v-if="currentLanguage === 'ar'" v-html="medical.activity_ar"></span>
            <span v-if="currentLanguage === 'en'" v-html="medical.activity_en"></span>
            <span v-if="currentLanguage === 'ru'" v-html="medical.activity_ru"></span>
            <span v-if="currentLanguage === 'zh'" v-html="medical.activity_zh"></span>
            <span v-if="currentLanguage === 'ja'" v-html="medical.activity_ja"></span>
            <span v-if="currentLanguage === 'ko'" v-html="medical.activity_ko"></span>
          </div>
        </div>

        <!-- Right Column -->
        <div>
          <h3 class="text-xl font-bold mb-4">{{ $t('contact_info') }}</h3>
          <div class="space-y-4">
            <div v-if="medical.phone" class="flex items-center">
              <i class="fas fa-phone text-blue mr-3"></i>
              <span>{{ medical.phone }}</span>
            </div>
            <div v-if="medical.email" class="flex items-center">
              <i class="fas fa-envelope text-blue mr-3"></i>
              <a :href="`mailto:${medical.email}`" class="text-blue hover:underline">{{ medical.email }}</a>
            </div>
            <div v-if="medical.website" class="flex items-center">
              <i class="fas fa-globe text-blue mr-3"></i>
              <a :href="medical.website" target="_blank" class="text-blue hover:underline">{{ medical.website }}</a>
            </div>
            <div v-if="medical.address" class="flex items-center">
              <i class="fas fa-map-marker-alt text-blue mr-3"></i>
              <span>{{ medical.address }}</span>
            </div>
            <div v-if="medical.ville" class="flex items-center">
              <i class="fas fa-city text-blue mr-3"></i>
              <span>
                <span v-if="currentLanguage === 'fr'">{{ medical.ville }}</span>
                <span v-if="currentLanguage === 'ar'">{{ medical.ville_ar }}</span>
                <span v-if="currentLanguage === 'en'">{{ medical.ville_en }}</span>
                <span v-if="currentLanguage === 'ru'">{{ medical.ville_ru }}</span>
                <span v-if="currentLanguage === 'zh'">{{ medical.ville_zh }}</span>
                <span v-if="currentLanguage === 'ja'">{{ medical.ville_ja }}</span>
                <span v-if="currentLanguage === 'ko'">{{ medical.ville_ko }}</span>
              </span>
            </div>
          </div>

          <!-- Video Section -->
          <div v-if="medical.video_link" class="mt-6">
            <h3 class="text-xl font-bold mb-4">{{ $t('video') }}</h3>
            <div class="aspect-video">
              <iframe
                :src="medical.video_link"
                class="w-full h-full rounded-lg"
                frameborder="0"
                allowfullscreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="text-center py-8">
    <p class="text-gray-500">{{ $t('not_found') }}</p>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import { metaTagsPage } from "../utils/filters";

export default {
  data() {
    return {
      currentLanguage: this.$i18n.locale,
      myMedicals: [],
    };
  },
  computed: {
    medical() {
        console.log("aaaaa ",this.myMedicals.find((x) => x.slug === this.$route.params.slug)?.medical_authorities?.find((y) => y.slug === this.$route.params.id));
      return this.myMedicals.find((x) => x.slug === this.$route.params.slug)?.medical_authorities?.find((y) => y.slug === this.$route.params.id) || null;
    },
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.currentLanguage = newLocale;
      this.addCanonicalLink();
      this.updateMetaTags();
    },
    medical() {
      this.updateMetaTags();
    },
  },
  methods: {
    ...mapActions(["fetchMedicals"]),
    async loadMedicals() {
      try {
        this.myMedicals = await this.fetchMedicals();
      } catch (error) {
        console.error("Error loading medicals: ", error);
      }
    },
    updateMetaTags() {
      if (this.medical) {
        let myUrl = 'sejour';
        metaTagsPage(this.medical, myUrl, this.currentLanguage);
      }
    },
    addCanonicalLink() {
      const currentUrl = window.location.href;
      const currentLanguage = this.currentLanguage;
      let canonicalLink = document.querySelector("link[rel='canonical']");

      const url = new URL(currentUrl);
      const pathSegments = url.pathname.split("/").filter(Boolean);

      if (currentLanguage === "ar" || currentLanguage === "en" || currentLanguage === "kor" || currentLanguage === "ru" || currentLanguage === 'zh' || currentLanguage === 'ja') {
        if (pathSegments[0] !== currentLanguage) {
          pathSegments.unshift(currentLanguage);
        }
      } else if (
        (currentLanguage === "fr" && pathSegments[0] === "ar") ||
        pathSegments[0] === "en" ||
        pathSegments[0] === "ru" || pathSegments[0] === 'ja' || pathSegments[0] === 'zh' || currentLanguage === "kor"
      ) {
        pathSegments.shift();
      }

      url.pathname = "/" + pathSegments.join("/");
      const canonicalUrl = url.toString();

      if (canonicalLink) {
        canonicalLink.setAttribute("href", canonicalUrl);
      } else {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        canonicalLink.setAttribute("href", canonicalUrl);
        document.head.appendChild(canonicalLink);
      }
    },
  },
  async created() {
    this.$store.dispatch("setLoading", true);
    try {
      await this.loadMedicals();
      this.updateMetaTags();
      this.addCanonicalLink();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this.$store.dispatch("setLoading", false);
    }
  },
};
</script>